import * as React from "react";
import "./inputCheckBox.css";
import { ReactComponent as ArrowDownIcon } from "../../assets/svgs/Chevrondown.svg";
import SearchFilterIcon from "../../assets/svgs/searchFilter.svg";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";

import {
  OptionsStatus,
  OptionsFlag,
  OptionsHarmon,
  OptionsCenter,
} from "../../utils/optionsAutocomplete";
import {
  CustomCheckbox,
  CustomCheckIcon,
} from "../customCheckbox/customCheckbox";

type Option = OptionsCenter | OptionsStatus | OptionsFlag | OptionsHarmon;

interface AutocompleteProps {
  value: Option[];
  onChange: (event: React.SyntheticEvent, newValue: Option[]) => void;
  placeholder?: string;
  inputAriaLabel?: string;
  width?: string | number;
  options: Option[];
  storageKey: string;

  setInputValue: (v: string) => void;
  inputValue: string;
  onSearchCenter?: (value: string) => void;
  isCenter?: boolean;
  minSearchLength?: number;
  isReadOnly?: boolean;
  disabled?: boolean;

  setSelectedOptionsT: any;
  clearState: () => void;
}

const formatStatus = (status: string) =>
  status
    .split("_")
    .map((word) =>
      word?.length <= 1
        ? word.toUpperCase()
        : word[0].toUpperCase() + word.slice(1),
    )
    .join(" ");

export default function InputCheckBox({
  value,
  onChange,
  placeholder,
  inputAriaLabel,
  width = "277px",
  options,
  storageKey,
  setInputValue,
  inputValue,
  onSearchCenter,
  isCenter = false,
  minSearchLength = 3,
  isReadOnly = false,
  disabled = false,
  setSelectedOptionsT,
  clearState,
}: Readonly<AutocompleteProps>) {
  const [open, setOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<Option[]>(
    Array.isArray(value) ? value : [],
  );

  const chipContainerRef = React.useRef<HTMLDivElement>(null);
  const suppressOpenRef = React.useRef(false);

  React.useEffect(() => {
    setSelectedOptions(Array.isArray(value) ? value : []);
  }, [value]);

  // Load from localStorage
  React.useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Option[];
        if (parsed?.length) {
          onChange({} as any, parsed);
        }
      } catch {}
    }
  }, []);

  React.useEffect(() => {
    if (
      chipContainerRef?.current &&
      typeof chipContainerRef.current.scrollTo === "function"
    ) {
      chipContainerRef.current.scrollTo({
        left: chipContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  }, [selectedOptions]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: Option[],
    reason: any,
  ) => {
    if (reason === "clear") {
      setInputValue("");
      setSelectedOptions([]);
      setSelectedOptionsT([]);
      clearState();
      setOpen(false);
      suppressOpenRef.current = true;
      return;
    }

    setSelectedOptions(newValue);
    onChange(event, newValue);

    setInputValue("");

    suppressOpenRef.current = false;

    if (isCenter) setOpen(true);
  };

  const shouldBeBlack = selectedOptions?.length > 0;
  const defaultFilter = createFilterOptions<Option>();

  const extractLastToken = (raw: string) => {
    if (!raw) return "";

    const byComma = raw.split(",");
    let candidate = byComma.at(-1)?.trim() ?? "";

    if (!candidate && raw.includes(" ")) {
      const bySpace = raw.split(/\s+/);
      candidate = bySpace.at(-1)?.trim() ?? "";
    }

    // Remove vírgula, ponto-e-vírgula e espaços no início
    while (
      candidate.startsWith(" ") ||
      candidate.startsWith(",") ||
      candidate.startsWith(";")
    ) {
      candidate = candidate.slice(1);
    }

    // Remove vírgula, ponto-e-vírgula e espaços no final
    while (
      candidate.endsWith(" ") ||
      candidate.endsWith(",") ||
      candidate.endsWith(";")
    ) {
      candidate = candidate.slice(0, -1);
    }

    return candidate;
  };

  const removeOptionByLabel = (label: string) => {
    const filtered = (selectedOptions ?? []).filter(
      (option) => option.label !== label,
    );

    setSelectedOptions(filtered);
    onChange(undefined as any, filtered);
  };

  return (
    <div style={{ position: "relative", width }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={disabled}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        popupIcon={null}
        open={isCenter ? options?.length > 0 && open : open}
        onOpen={() => {
          if (isCenter) {
            if (suppressOpenRef.current && !inputValue) return;
            return setOpen(true);
          }
          setOpen(true);
        }}
        onClose={(e, reason) => {
          if (isCenter) {
            if (["blur", "escape", "toggle"].includes(reason)) {
              suppressOpenRef.current = true;
              setOpen(false);
            }
            return;
          }
          suppressOpenRef.current = true;
          setOpen(false);
        }}
        inputValue={isReadOnly ? "" : inputValue}
        onInputChange={(_, newInputValue, reason) => {
          if (isReadOnly) return;
          if (reason === "reset") return;

          if (reason === "input") {
            suppressOpenRef.current = false;
          }

          if (!isCenter) {
            setInputValue(newInputValue);
            return;
          }

          const lastTyped = extractLastToken(newInputValue);
          setInputValue(lastTyped);

          if (lastTyped?.length >= minSearchLength && onSearchCenter) {
            onSearchCenter(lastTyped);
            setOpen(true);
          } else {
            setOpen(false);
          }
        }}
        isOptionEqualToValue={(option, val) => option.label === val.label}
        getOptionLabel={(opt) => opt.label || ""}
        filterOptions={(opts, state) =>
          isCenter ? opts : defaultFilter(opts, state)
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} className="sizeList">
            <CustomCheckbox
              icon={
                <div style={{ width: 24, height: 24, background: "#71767C" }} />
              }
              checkedIcon={<CustomCheckIcon />}
              checked={selected}
            />
            {formatStatus(option.label || "")}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            focused
            variant="filled"
            placeholder={selectedOptions?.length === 0 ? placeholder : ""}
            inputProps={{
              ...params.inputProps,
              "aria-label": inputAriaLabel ?? placeholder,
            }}
            onClick={(e) => {
              if (isCenter) {
                if (suppressOpenRef.current && !inputValue) {
                  e.stopPropagation();
                }
              }
            }}
            InputProps={{
              ...params.InputProps,
              readOnly: isReadOnly,
              startAdornment: (
                <div
                  ref={chipContainerRef}
                  data-testid="chip-container"
                  style={{
                    maxWidth: "100%",
                    display: "flex",
                    alignItems: "center",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    paddingBottom: "2px",
                    flexShrink: 0,
                    position: "relative",
                  }}
                  onWheel={(e) => {
                    const el = e.currentTarget;
                    if (e.deltaY !== 0) {
                      el.scrollLeft += e.deltaY;
                      e.preventDefault();
                    }
                  }}
                >
                  <style>{`
                    div::-webkit-scrollbar { height: 6px; }
                    div::-webkit-scrollbar-track { background: #f0f0f0; border-radius: 10px; }
                    div::-webkit-scrollbar-thumb { background: #b8b8b8; border-radius: 10px; }
                    div::-webkit-scrollbar-thumb:hover { background: #9a9a9a; }
                    div { scrollbar-width: thin; scrollbar-color: #b8b8b8 #f0f0f0; }
                  `}</style>

                  {(selectedOptions || []).map((opt) => (
                    <div
                      key={opt.label}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "#e0e0e0",
                        padding: "2px 6px",
                        borderRadius: "12px",
                        marginRight: "4px",
                        fontSize: "13px",
                        flexShrink: 0,
                      }}
                    >
                      {opt.label}
                      <button
                        style={{
                          marginLeft: "6px",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOptionByLabel(opt.label);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiInputBase-input": {
                paddingLeft: "4px",
              },
              "& .MuiFilledInput-root:before": {
                borderBottom: "1px solid black !important",
              },
              "& .MuiFilledInput-root:after": {
                borderBottom: "1px solid black !important",
              },
              "& .MuiInputBase-input::placeholder": {
                color: shouldBeBlack ? "black" : "grey",
                opacity: shouldBeBlack ? 1 : 0.6,
                fontSize: shouldBeBlack ? 15 : undefined,
              },
            }}
          />
        )}
      />

      {/* Ícone */}
      <div
        style={{
          position: "absolute",
          right: "11.5px",
          top: "45%",
          transform: "translateY(-20.5%)",
          pointerEvents: "none",
        }}
      >
        {isCenter ? (
          <img
            style={{ marginLeft: "5px", width: "18px" }}
            className="iconMessage"
            src={SearchFilterIcon}
            alt="icon-alert"
          />
        ) : (
          <ArrowDownIcon />
        )}
      </div>
    </div>
  );
}
